import React, { useState } from 'react';
import { getAuthToken } from '../../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HumanizeModule = ({ user }) => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    level: 'natural',
    creativity: 50,
    style: 'professional',
    targetAudience: 'general'
  });

  const humanizationLevels = [
    {
      id: 'standard',
      name: 'Standard',
      desc: 'Corrections basiques et fluidité améliorée',
      icon: '📝',
      features: ['Correction grammaticale', 'Fluidité de base', 'Ponctuation optimisée'],
      color: 'blue'
    },
    {
      id: 'natural',
      name: 'Naturel',
      desc: 'Style conversationnel et expressions humaines',
      icon: '💬',
      features: ['Expressions naturelles', 'Transitions fluides', 'Ton conversationnel'],
      color: 'green',
      recommended: true
    },
    {
      id: 'premium',
      name: 'Premium',
      desc: 'Réécriture approfondie avec variations stylistiques',
      icon: '✨',
      features: ['Vocabulaire riche', 'Métaphores subtiles', 'Rythme varié'],
      color: 'purple'
    },
    {
      id: 'expert',
      name: 'Expert Humain',
      desc: 'Qualité rédacteur professionnel indétectable',
      icon: '🎯',
      features: ['Style unique', 'Nuances émotionnelles', 'Authenticité totale'],
      color: 'amber'
    }
  ];

  const writingStyles = [
    { id: 'professional', name: 'Professionnel', emoji: '👔' },
    { id: 'casual', name: 'Décontracté', emoji: '😊' },
    { id: 'academic', name: 'Académique', emoji: '🎓' },
    { id: 'journalistic', name: 'Journalistique', emoji: '📰' },
    { id: 'storytelling', name: 'Narratif', emoji: '📖' },
    { id: 'marketing', name: 'Marketing', emoji: '📢' }
  ];

  const targetAudiences = [
    { id: 'general', name: 'Grand public' },
    { id: 'professionals', name: 'Professionnels' },
    { id: 'youth', name: 'Jeunes (18-25)' },
    { id: 'seniors', name: 'Seniors (50+)' },
    { id: 'experts', name: 'Experts du domaine' },
    { id: 'beginners', name: 'Débutants' }
  ];

  const handleHumanize = async () => {
    if (!inputText.trim()) return;
    
    setProcessing(true);
    setOutputText('');
    setError('');

    try {
      const token = getAuthToken();
      
      const humanizePrompt = `
Tu es un expert en réécriture de texte. Ta mission est d'humaniser ce texte en le rendant plus naturel et authentique.

NIVEAU D'HUMANISATION: ${settings.level.toUpperCase()}
- Standard: Corrections basiques, améliorer la fluidité
- Naturel: Ajouter des expressions humaines, transitions naturelles
- Premium: Réécriture créative avec variations stylistiques
- Expert: Qualité indiscernable d'un rédacteur humain professionnel

STYLE: ${settings.style}
PUBLIC CIBLE: ${settings.targetAudience}
NIVEAU DE CRÉATIVITÉ: ${settings.creativity}% (0=fidèle, 100=très créatif)

RÈGLES:
1. Supprimer les formulations robotiques
2. Varier le vocabulaire et la structure des phrases
3. Ajouter des transitions naturelles
4. Utiliser un ton ${settings.style}
5. Adapter au public cible: ${settings.targetAudience}
6. Conserver le sens et les informations clés
7. ${settings.level === 'expert' ? 'Rendre le texte totalement indétectable comme généré par IA' : ''}

TEXTE À HUMANISER:
${inputText}

TEXTE HUMANISÉ:`;

      const response = await fetch(`${API_URL}/api/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content_type: 'blog',
          topic: 'Humanisation de texte',
          tone: settings.style,
          word_count: inputText.split(' ').length * 1.2,
          additional_instructions: humanizePrompt
        })
      });

      if (response.ok) {
        const data = await response.json();
        setOutputText(data.content);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de l\'humanisation');
      }
    } catch (error) {
      console.error('Humanization error:', error);
      setError(error.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: isActive ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'border-gray-700 hover:border-blue-500/30',
      green: isActive ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'border-gray-700 hover:border-green-500/30',
      purple: isActive ? 'bg-purple-500/20 border-purple-500/50 text-purple-400' : 'border-gray-700 hover:border-purple-500/30',
      amber: isActive ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' : 'border-gray-700 hover:border-amber-500/30'
    };
    return colors[color] || colors.green;
  };

  return (
    <div className="space-y-6">
      {/* Humanization Levels */}
      <div className="grid grid-cols-4 gap-4">
        {humanizationLevels.map(level => (
          <button
            key={level.id}
            onClick={() => setSettings({...settings, level: level.id})}
            className={`relative p-4 rounded-2xl border-2 transition-all text-left ${getColorClasses(level.color, settings.level === level.id)}`}
          >
            {level.recommended && (
              <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                RECOMMANDÉ
              </span>
            )}
            <div className="text-2xl mb-2">{level.icon}</div>
            <h4 className={`font-bold mb-1 ${settings.level === level.id ? '' : 'text-white'}`}>{level.name}</h4>
            <p className="text-xs text-gray-500 mb-3">{level.desc}</p>
            <ul className="space-y-1">
              {level.features.map((feature, idx) => (
                <li key={idx} className="text-xs text-gray-400 flex items-center">
                  <svg className="w-3 h-3 mr-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>

      {/* Settings Row */}
      <div className="grid grid-cols-3 gap-6">
        {/* Creativity Slider */}
        <div className="bg-[#12121a] rounded-2xl border border-gray-800 p-4">
          <label className="block text-sm text-gray-400 mb-3">Niveau de créativité</label>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="100"
              value={settings.creativity}
              onChange={(e) => setSettings({...settings, creativity: parseInt(e.target.value)})}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>Fidèle</span>
              <span className="text-green-400 font-bold">{settings.creativity}%</span>
              <span>Créatif</span>
            </div>
          </div>
        </div>

        {/* Writing Style */}
        <div className="bg-[#12121a] rounded-2xl border border-gray-800 p-4">
          <label className="block text-sm text-gray-400 mb-3">Style rédactionnel</label>
          <div className="flex flex-wrap gap-2">
            {writingStyles.map(style => (
              <button
                key={style.id}
                onClick={() => setSettings({...settings, style: style.id})}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  settings.style === style.id
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {style.emoji} {style.name}
              </button>
            ))}
          </div>
        </div>

        {/* Target Audience */}
        <div className="bg-[#12121a] rounded-2xl border border-gray-800 p-4">
          <label className="block text-sm text-gray-400 mb-3">Public cible</label>
          <select
            value={settings.targetAudience}
            onChange={(e) => setSettings({...settings, targetAudience: e.target.value})}
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
          >
            {targetAudiences.map(audience => (
              <option key={audience.id} value={audience.id}>{audience.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Input/Output Area */}
      <div className="grid grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">Texte original</h3>
              <p className="text-xs text-gray-500">{inputText.split(' ').filter(w => w).length} mots</p>
            </div>
            <button
              onClick={() => setInputText('')}
              className="text-xs text-gray-500 hover:text-white transition-colors"
            >
              Effacer
            </button>
          </div>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Collez ici le texte à humaniser...

Exemple: Le texte généré par IA qui semble robotique et nécessite une touche humaine pour paraître plus naturel et engageant."
            data-testid="hum-input"
            className="flex-1 bg-transparent p-4 text-gray-300 placeholder-gray-600 resize-none focus:outline-none min-h-[300px]"
          />
        </div>

        {/* Output */}
        <div className="bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">Texte humanisé</h3>
              <p className="text-xs text-gray-500">
                {outputText ? `${outputText.split(' ').filter(w => w).length} mots` : 'En attente'}
              </p>
            </div>
            {outputText && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => copyToClipboard(outputText)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
                  title="Copier"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 p-4 min-h-[300px]" data-testid="hum-output">
            {processing ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <div className="w-12 h-12 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin mb-4"></div>
                <p>Humanisation en cours...</p>
                <p className="text-xs mt-1">Niveau: {humanizationLevels.find(l => l.id === settings.level)?.name}</p>
              </div>
            ) : error ? (
              <div className="h-full flex flex-col items-center justify-center text-red-400">
                <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <p className="text-center font-medium">{error}</p>
                <button 
                  onClick={() => setError('')}
                  className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-white transition-colors"
                >
                  Fermer
                </button>
              </div>
            ) : outputText ? (
              <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                {outputText}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-600 text-center">
                <div>
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                  </svg>
                  <p>Le texte humanisé<br/>apparaîtra ici</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleHumanize}
        disabled={processing || !inputText.trim()}
        data-testid="hum-run-btn"
        className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 rounded-xl font-semibold text-white transition-all flex items-center justify-center space-x-2 shadow-lg shadow-purple-500/20"
      >
        {processing ? (
          <>
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span>Humanisation en cours...</span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span>Humaniser le texte</span>
          </>
        )}
      </button>
    </div>
  );
};

export default HumanizeModule;
